import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  Users,
  Target,
  TrendingUp,
  CheckCircle,
  Star,
  FileText,
  Search,
  Filter,
  Plus,
  ArrowRight,
  Clock,
  DollarSign,
  Globe,
  Zap,
  Shield,
  Award,
  BarChart3,
  MessageSquare,
  Calendar,
  MapPin,
  Briefcase,
  Mail,
  Phone,
  Play,
  Download,
  Lightbulb,
  Cpu,
  Database,
  Cloud,
  Lock,
  Settings,
  PieChart,
  GraduationCap,
} from "lucide-react";
import CommonContactUs from "./CommonContactUs";

const ForCompaniesPage = () => {
  const [activeTab, setActiveTab] = useState("solutions");

  const solutions = [
    {
      icon: <Users className="w-8 h-8 text-blue-600" />,
      title: "Talent Acquisition",
      description:
        "Find the best tech talent for your organization with our comprehensive recruitment solutions.",
      features: [
        "High-volume hiring",
        "Multi-location support",
        "Custom workflows",
        "Advanced analytics",
      ],
      link: "/talent-acquisition",
    },
    {
      icon: <Cpu className="w-8 h-8 text-green-600" />,
      title: "Employer Branding",
      description:
        "Build a strong employer brand that attracts top talent and showcases your company culture.",
      features: [
        "HRIS integration",
        "Automated workflows",
        "Performance management",
        "Employee self-service",
      ],
      link: "/hr-consulting",
    },
    {
      icon: <Database className="w-8 h-8 text-purple-600" />,
      title: "HR Consulting",
      description:
        "Strategic human resources consulting to optimize your talent acquisition and retention strategies.",
      features: [
        "Predictive analytics",
        "Talent forecasting",
        "ROI measurement",
        "Custom reporting",
      ],
      link: "/job-matching",
    },
    {
      icon: <Users className="w-8 h-8 text-orange-600" />,
      title: "Contract Staffing",
      description: "Contract Staffing",
      features: [
        "Cloud deployment",
        "API integration",
        "Mobile access",
        "Real-time updates",
      ],
      link: "/tech-training",
    },
  ];

  const services = [
    {
      icon: <Users className="w-8 h-8 text-blue-600" />,
      title: "Strategic Partnership",
      description:
        "Long-term partnerships to support your business growth and talent needs.",
      features: [
        "Dedicated account manager",
        "Custom solutions",
        "Priority support",
        "Regular reviews",
      ],
    },
    {
      icon: <Lightbulb className="w-8 h-8 text-green-600" />,
      title: "Consulting Services",
      description:
        "Expert consulting to optimize your HR and recruitment strategies.",
      features: [
        "Process optimization",
        "Technology assessment",
        "Change management",
        "Best practices",
      ],
    },
    {
      icon: <Shield className="w-8 h-8 text-purple-600" />,
      title: "Compliance Support",
      description:
        "Ensure your hiring practices meet all legal and regulatory requirements.",
      features: [
        "Legal compliance",
        "Audit support",
        "Policy development",
        "Training programs",
      ],
    },
    {
      icon: <Settings className="w-8 h-8 text-orange-600" />,
      title: "System Integration",
      description:
        "Seamless integration with your existing HR and business systems.",
      features: [
        "API development",
        "Data migration",
        "Custom workflows",
        "Ongoing support",
      ],
    },
  ];

  const benefits = [
    {
      icon: <TrendingUp className="w-8 h-8 text-blue-600" />,
      title: "Scalable Growth",
      description:
        "Solutions that grow with your business, from startup to enterprise.",
    },
    {
      icon: <DollarSign className="w-8 h-8 text-green-600" />,
      title: "Cost Optimization",
      description:
        "Reduce recruitment costs while improving efficiency and quality.",
    },
    {
      icon: <Globe className="w-8 h-8 text-purple-600" />,
      title: "Global Reach",
      description:
        "Access talent worldwide with our international recruitment network.",
    },
    {
      icon: <Zap className="w-8 h-8 text-orange-600" />,
      title: "Fast Implementation",
      description:
        "Quick deployment and onboarding to get you up and running fast.",
    },
  ];

  const testimonials = [
    {
      name: "Robert Johnson",
      role: "VP of HR",
      company: "Fortune 500 Tech Corp",
      content:
        "TechMind's enterprise solutions helped us scale our hiring by 300% while reducing costs by 40%.",
      rating: 5,
    },
    {
      name: "Lisa Chen",
      role: "Chief People Officer",
      company: "Global Manufacturing Inc",
      content:
        "Their consulting services transformed our HR processes and improved employee satisfaction significantly.",
      rating: 5,
    },
    {
      name: "Michael Rodriguez",
      role: "CTO",
      company: "FinTech Startup",
      content:
        "The system integration was seamless, and their support team is incredibly responsive.",
      rating: 5,
    },
  ];

  const resources = [
    {
      title: "Enterprise Recruitment Guide",
      description:
        "Comprehensive guide for scaling recruitment in large organizations.",
      icon: <FileText className="w-6 h-6" />,
      link: "#",
    },
    {
      title: "HR Technology Assessment",
      description:
        "Framework for evaluating and selecting HR technology solutions.",
      icon: <BarChart3 className="w-6 h-6" />,
      link: "#",
    },
    {
      title: "Compliance Checklist",
      description: "Essential compliance requirements for enterprise hiring.",
      icon: <Shield className="w-6 h-6" />,
      link: "#",
    },
    {
      title: "ROI Calculator",
      description:
        "Calculate the return on investment for recruitment solutions.",
      icon: <PieChart className="w-6 h-6" />,
      link: "#",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#1A193B] pt-20 text-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-3 lg:px-4 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              For Companies
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-indigo-100">
              Strategic workforce planning and talent management solutions_
            </p>

            {/* <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="px-8 py-3 bg-white text-indigo-600 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Schedule Demo
              </Link>
              <Link 
                to="/services"
                className="px-8 py-3 border border-white text-white rounded-md hover:bg-white hover:text-indigo-600 transition-colors font-medium"
              >
                View Solutions
              </Link>
            </div> */}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Enterprise Companies Choose TechMind
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven solutions trusted by Fortune 500 companies and growing enterprises worldwide
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Solutions Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {/* Enterprise Solutions */}
              Services for Companies
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              TechMind offers comprehensive workforce planning and talent
              management solutions for companies of all sizes. We help
              organizations build and maintain high-performing teams through
              strategic talent acquisition and management.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                className="bg-white rounded-lg shadow-sm border p-8 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-6">
                  {solution.icon}
                  <h3 className="text-2xl font-semibold text-gray-900 ml-4">
                    {solution.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6 text-lg">
                  {solution.description}
                </p>
                {/* <ul className="space-y-3 mb-6">
                  {solution.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-600">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul> */}
                <Link
                  to={solution.link}
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-lg"
                >
                  Learn More
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Professional Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert services to support your implementation and ongoing success
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-center mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4 text-center">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Partner With TechMind
            </h2>
            {/* <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A structured approach to ensure successful implementation and adoption
            </p> */}
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Strategic Partnership",
                description:
                  "We work as an extension of your team, understanding your business goals and aligning our talent solutions accordingly.",
              },
              {
                step: "02",
                title: "Comprehensive Solutions",
                description:
                  "From talent acquisition to workforce management, we provide end-to-end solutions tailored to your specific needs.",
              },
              {
                step: "03",
                title: "Industry Expertise",
                description:
                  "Our specialized knowledge of the tech industry ensures we understand the unique talent challenges and opportunities in your sector.",
              },
              {
                step: "04",
                title: "Measurable Results",
                description:
                  "We focus on delivering tangible outcomes, from reduced time-to-hire to improved retention rates and enhanced team performance.",
              },
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {process.title}
                </h3>
                <p className="text-gray-600 text-sm">{process.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Enterprise Resources
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Access our library of enterprise-focused resources and tools
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {resources.map((resource, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.1 + index * 0.1 }}
                className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-4 text-indigo-600">
                  {resource.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {resource.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {resource.description}
                </p>
                <Link 
                  to={resource.link}
                  className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Download
                  <Download className="w-4 h-4 ml-1" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Testimonials Section */}
      {/* <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Enterprise Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from enterprise leaders who have transformed their organizations with TechMind
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.3 + index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-sm"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">
                  "{testimonial.content}"
                </p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role} at {testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="flex justify-center gap-4 flex-wrap mb-8"
      >
        <Link to="/services">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gray-100 text-gray-800 hover:bg-gray-200 px-6 py-3 rounded-lg font-medium transition-colors shadow-md inline-flex items-center"
          >
            <span>Back to Services</span>
          </motion.button>
        </Link>
        <Link to="/contact">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-lg flex items-center space-x-2 transition-colors duration-300"
          >
            <GraduationCap size={18} />
            <span>Contact Us</span>
          </motion.button>
        </Link>
      </motion.div>

      <section className="py-16 bg-[#1A193B] text-white">
        <CommonContactUs/>
      </section>
    </div>
  );
};

export default ForCompaniesPage;
